import React, { useRef, useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";

import { SearchIcon } from "../icons/SearchIcon";
import CloseIcon from "../icons/CloseIcon";

import { useAppStore } from "../../hooks/useAppStore";
import { apiService } from "../../services/apiService";

const DEBOUNCE_MS = 300;

const ChatSearch: React.FC = observer(() => {
  const { searchQuery, setSearchQuery, setSearchResults, setSearchLoading } =
    useAppStore();
  const [inputValue, setInputValue] = useState(searchQuery);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  const performSearch = async (query: string) => {
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    if (!query.trim() || query.trim().length < 3) {
      setSearchResults([]);
      searchAbortControllerRef.current = null;
      return;
    }

    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    try {
      setSearchLoading(true);
      const response = await apiService.searchChats(
        query,
        50,
        undefined,
        abortController.signal,
      );

      if (abortController.signal.aborted) return;

      if (response.success && response.data) {
        setSearchResults(response.data.results);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      setSearchResults([]);
    } finally {
      if (!abortController.signal.aborted) {
        setSearchLoading(false);
        searchAbortControllerRef.current = null;
      }
    }
  };

  // Always points to the latest performSearch so the debounce callback never goes stale
  const performSearchRef = useRef(performSearch);
  performSearchRef.current = performSearch;

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const query = e.target.value;
    setInputValue(query);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(query);
      performSearchRef.current(query);
    }, DEBOUNCE_MS);
  };

  const handleClearSearch = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setInputValue("");
    setSearchQuery("");
    setSearchResults([]);
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      searchAbortControllerRef.current = null;
    }
  };

  const toggleDeepSearch = () => {
    if (!!inputValue) {
      performSearch(inputValue);
    }
  };

  useEffect(() => {
    return () => {
      if (searchAbortControllerRef.current)
        searchAbortControllerRef.current.abort();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        pl: 2,
        pr: 1,
        py: 1.5,
        alignItems: "flex-start",
        alignSelf: "stretch",
        flexShrink: 0,
        flex: 1,
      }}
    >
      <TextField
        fullWidth
        value={inputValue}
        onChange={handleSearchChange}
        placeholder="Search chats..."
        variant="standard"
        size="small"
        sx={{ height: "40px" }}
        InputProps={{
          disableUnderline: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleClearSearch}
                size="small"
                sx={{ p: 1 }}
              >
                {inputValue ? <CloseIcon size={16} /> : <SearchIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
});

export default ChatSearch;
